import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { AppRole } from '../types/auth';
import { useAuth } from './useAuth';

interface RoleGuardProps {
  /** The QMS App Roles permitted to view the guarded children. */
  allowedRoles: readonly AppRole[];
  children: ReactNode;
  /**
   * Inside the shell (13-08): send a signed-in user without the role here instead of showing the
   * access state, e.g. a Care Manager who follows a link to /verify lands back on Today.
   */
  redirectTo?: string;
}

/**
 * Role-based guard for the QMS admin shell — renders children only if the
 * signed-in user carries at least one of the allowed **Entra App Roles**
 * (`idTokenClaims.roles`, 04-CONTEXT.md §2). Otherwise it shows a clear
 * access state and distinguishes two cases:
 *
 *   1. The login has NO QMS role at all → "No QMS role assigned" (the common
 *      first-login state before Andy provisions the App Role in Entra — see
 *      04-04-rbac-resolved.md §5). Tells the user to contact Andy.
 *   2. The login has SOME QMS role but not one this page needs → "Access
 *      denied" (the conventional wrong-role state).
 */
export default function RoleGuard({ allowedRoles, children, redirectTo }: RoleGuardProps) {
  const { roles, logout } = useAuth();

  const hasAccess = roles.some((r) => allowedRoles.includes(r));
  if (hasAccess) {
    return <>{children}</>;
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  const hasNoRole = roles.length === 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'Montserrat, system-ui, sans-serif',
      }}
    >
      <h1 style={{ color: '#7f3f96', marginBottom: '0.5rem' }}>
        {hasNoRole ? 'No QMS role assigned' : 'Access denied'}
      </h1>
      {hasNoRole ? (
        <p style={{ color: '#555', marginBottom: '1.5rem', maxWidth: '32rem' }}>
          Your Harmony account is signed in, but it doesn&apos;t have a Harmony
          QMS role yet. Ask Andy to assign you a QMS role (Oversight, Care
          Manager, HR/WHS, or Finance) in the QMS app, then sign in again.
        </p>
      ) : (
        <p style={{ color: '#555', marginBottom: '1.5rem', maxWidth: '32rem' }}>
          You don&apos;t have permission to view this page with your current
          QMS role.
        </p>
      )}
      <p style={{ color: '#777', marginBottom: '2rem' }}>
        Your current roles:{' '}
        {roles.length > 0 ? roles.join(', ') : 'none assigned'}
      </p>
      <button
        onClick={() => void logout()}
        style={{
          padding: '0.5rem 1.5rem',
          fontSize: '1rem',
          borderRadius: '6px',
          border: '1px solid #ccc',
          backgroundColor: '#f5f5f5',
          cursor: 'pointer',
        }}
      >
        Sign out
      </button>
    </div>
  );
}
